/**
 * Icon - port 1:1 z new-design/ui.jsx PATHS object.
 *
 * Stroke icons w jednym SVG viewbox 24x24, currentColor + strokeWidth 1.8.
 * Wszystkie ikony z designu zachowane bez zmian - mapa name -> SVG path string.
 */
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

const PATHS: Record<string, string> = {
  wand: '<path d="M15 4V2m0 18v-2M9 3l-1 1m12 12l-1 1M4 9H2m18 6h2M5 19L19 5m-3 0l3 3"/>',
  eraser: '<path d="M7 21h12M5 13l6-6 7 7-5 5H9l-4-4a1.4 1.4 0 010-2z"/>',
  sofa: '<path d="M4 11V8a2 2 0 012-2h12a2 2 0 012 2v3m-16 0a2 2 0 00-2 2v3h20v-3a2 2 0 00-2-2m-16 0a2 2 0 012 2v1h12v-1a2 2 0 012-2M5 18v2m14-2v2"/>',
  ruler: '<path d="M3 8.5L8.5 3 21 15.5 15.5 21 3 8.5zm4 0l1.5 1.5M10 5.5L11.5 7m-1 5l1.5 1.5M13.5 9L15 10.5"/>',
  camera: '<path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/><circle cx="12" cy="12.5" r="3.2"/>',
  folder: '<path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/>',
  settings:
    '<circle cx="12" cy="12" r="3"/><path d="M12 2v3m0 14v3M4.2 4.2l2.1 2.1m11.4 11.4l2.1 2.1M2 12h3m14 0h3M4.2 19.8l2.1-2.1m11.4-11.4l2.1-2.1"/>',
  coins:
    '<ellipse cx="9" cy="7" rx="6" ry="3"/><path d="M3 7v5c0 1.7 2.7 3 6 3s6-1.3 6-3"/><path d="M9 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5c0-1.7-2.7-3-6-3"/>',
  plus: '<path d="M12 5v14m-7-7h14"/>',
  minus: '<path d="M5 12h14"/>',
  // `loader` i `send` były wołane z kodu, ale nie istniały w mapie — Icon rysował
  // wtedy pusty <svg> bez żadnego błędu (patrz __tests__/iconNamesExist.test.ts).
  loader: '<path d="M12 3a9 9 0 109 9"/>',
  chart: '<path d="M4 20V10m5 10V4m5 16v-7m5 7V8"/><path d="M3 20h18"/>',
  map: '<path d="M9 4L3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4z"/><path d="M9 4v13m6-10.5V19.5"/>',
  send: '<path d="M21.5 2.5L2.5 10l7.5 3.5L13.5 21l8-18.5z"/><path d="M10 13.5l4-4"/>',
  mic: '<rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0014 0M12 18v3m-3 0h6"/>',
  // Podgląd zdjęcia: dopasowanie do ekranu, powiększenie, duplikowanie kształtu.
  maximize: '<path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"/>',
  zoomIn: '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5M11 8v6M8 11h6"/>',
  hand: '<path d="M7 11V5.5a1.5 1.5 0 013 0V11m0-1V4.5a1.5 1.5 0 013 0V11m0-.5V6a1.5 1.5 0 013 0v5m0-2a1.5 1.5 0 013 0v5.5a6.5 6.5 0 01-6.5 6.5h-1a6 6 0 01-4.6-2.1L6 17c-1-1.2-1.4-2-1.4-2a1.6 1.6 0 012.4-2L9 14.5"/>',
  crop: '<path d="M6 2v14a2 2 0 002 2h14"/><path d="M18 22V8a2 2 0 00-2-2H2"/>',
  // Edytor rzutu — narzędzia rysunkowe.
  cursor: '<path d="M5 3l6 15 2.2-6.3L19.5 9.5 5 3z"/>',
  wall: '<path d="M3 7h18M3 12h18M3 17h18M9.5 7v5M15 12v5M7 17v-5"/>',
  room: '<path d="M4 4h16v16H4V4z"/><path d="M4 13h3.5M16.5 13H20"/>',
  textT: '<path d="M5 5h14M12 5v14M9.5 19h5"/>',
  undo: '<path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 016 6v1"/>',
  rotate: '<path d="M20.5 12a8.5 8.5 0 11-2.6-6.1"/><path d="M20.5 3.5v5h-5"/>',
  grid: '<path d="M3 3h18v18H3V3z"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/>',
  x: '<path d="M6 6l12 12M18 6L6 18"/>',
  chevR: '<path d="M9 6l6 6-6 6"/>',
  chevD: '<path d="M6 9l6 6 6-6"/>',
  download: '<path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16"/>',
  refresh: '<path d="M3 12a9 9 0 0115.5-6.3L21 8M21 4v4h-4M21 12a9 9 0 01-15.5 6.3L3 16m0 4v-4h4"/>',
  lock: '<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 018 0v3"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>',
  eye: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  eyeOff: '<path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"/>',
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  copy: '<rect x="9" y="9" width="12" height="12" rx="2"/><path d="M6 15H5a2 2 0 01-2-2V5a2 2 0 012-2h8a2 2 0 012 2v1"/>',
  arrowL: '<path d="M19 12H5m0 0l6-6m-6 6l6 6"/>',
  arrowR: '<path d="M5 12h14m0 0l-6-6m6 6l-6 6"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
  upload: '<path d="M12 16V4m0 0L8 8m4-4l4 4M4 20h16"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  dots: '<circle cx="12" cy="5" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="12" cy="19" r="1.4"/>',
  edit: '<path d="M16 3l5 5M4 20l1-4L17 4l3 3L8 19l-4 1z"/>',
  star: '<path d="M12 3l2.7 5.7 6.3.9-4.6 4.4 1.1 6.3L12 17.8 6.5 20.3l1.1-6.3L3 9.6l6.3-.9L12 3z"/>',
  home: '<path d="M3 11l9-8 9 8M5 9.5V21h14V9.5"/>',
  archive: '<rect x="3" y="4" width="18" height="5" rx="1"/><path d="M5 9v10a1 1 0 001 1h12a1 1 0 001-1V9M10 13h4"/>',
  alert: '<path d="M12 3l9 16H3L12 3z"/><path d="M12 9v5m0 3v.5"/>',
  layers: '<path d="M12 3l9 5-9 5-9-5 9-5zm9 9l-9 5-9-5m18 4l-9 5-9-5"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5m0-8v.5"/>',
  image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.6"/><path d="M21 16l-5-5L5 20"/>',
  brush: '<path d="M14 4l6 6-9 9H5v-6l9-9zm-3 3l5 5"/>',
  pencil: '<path d="M4 20l1-4L16 5l3 3L8 19l-4 1z"/>',
  move:
    '<path d="M12 3v18M3 12h18M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"/>',
  grip:
    '<circle cx="9" cy="6" r="1.3"/><circle cx="15" cy="6" r="1.3"/><circle cx="9" cy="12" r="1.3"/><circle cx="15" cy="12" r="1.3"/><circle cx="9" cy="18" r="1.3"/><circle cx="15" cy="18" r="1.3"/>',
  gift: '<rect x="3" y="9" width="18" height="12" rx="1"/><path d="M3 13h18M12 9v12M12 9S10 3 7 5s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z"/>',
  trash: '<path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/>',
  split: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 4v16"/>',
  growth: '<path d="M3 13l5-5 4 4 6-7"/><path d="M14 5h5v5"/><path d="M3 20h18"/>',
  building:
    '<path d="M3 21h18M6 21V5a1 1 0 011-1h6a1 1 0 011 1v16M14 21V9h3a1 1 0 011 1v11M8 8h2M8 12h2M8 16h2"/>',
  users:
    '<circle cx="9" cy="8" r="3.4"/><path d="M2.5 20c0-3.4 3-5.2 6.5-5.2s6.5 1.8 6.5 5.2"/><path d="M16.5 5a3.2 3.2 0 010 6.2M18 14.8c2.2.5 3.5 2 3.5 4.2"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/>',
  phone:
    '<path d="M4 5c0 9 6 15 15 15l1.5-3.5-4-2-1.6 1.6c-2.4-1.2-4.3-3.1-5.5-5.5L11 9 9 5H4z"/>',
  // Dymek czatu ze słuchawką — czytelny „napisz do nas" w stylu WhatsAppa.
  chat:
    '<path d="M20.5 11.5a8.5 8.5 0 01-12.4 7.6L3.5 20.5l1.4-4.6A8.5 8.5 0 1120.5 11.5z"/><path d="M9 9.2c0 3.2 2.1 5.3 5.3 5.3l.7-1.6-1.9-.9-.7.7c-.9-.5-1.6-1.2-2.1-2.1l.7-.7-.9-1.9L9 9.2z"/>',
  link: '<path d="M9.5 14.5l5-5M10.5 6.8l1.6-1.6a3.8 3.8 0 015.4 5.4l-2 2M13.5 17.2l-1.6 1.6a3.8 3.8 0 01-5.4-5.4l2-2"/>',
  shield:
    '<path d="M12 3l8 3v6c0 4.6-3.3 7.8-8 9-4.7-1.2-8-4.4-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/>',
  crown: '<path d="M3 8l4 3.5L12 5l5 6.5L21 8l-1.6 11H4.6L3 8z"/>',
  pause: '<path d="M9 5v14M15 5v14"/>',
  play: '<path d="M7 5l12 7-12 7V5z"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z"/>',
  file: '<path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z"/><path d="M14 3v5h5M9 13h6M9 17h4"/>',
  megaphone:
    '<path d="M4 10v4a1 1 0 001 1h2l7 4V5L7 9H5a1 1 0 00-1 1z"/><path d="M18 9.5a3.5 3.5 0 010 5"/>',
};

/**
 * Nazwy dostępnych ikon. Eksportowane, bo katalogi danych (np. kategorie oceny
 * stanu w `@adresflow-v2/shared`) trzymają nazwę ikony jako string i nie mają jak
 * jej sprawdzić — nieznana nazwa renderuje PUSTE svg, bez błędu i bez śladu.
 */
export const ICON_NAMES = Object.keys(PATHS);

interface IconProps {
  name: string;
  style?: CSSProperties;
  className?: string;
}

export function Icon({ name, style, className }: IconProps) {
  const path = PATHS[name] ?? "";
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      // Domyślny rozmiar jako SIATKA BEZPIECZEŃSTWA, nie jako sposób nadawania
      // rozmiaru. W tym kodzie ikony rozmiarowane są regułą rodzica w legacy.css
      // (`.btn svg`, `.navitem svg`, …), która ma wyższą specyficzność i wygrywa;
      // `style` i klasa od wołającego (twMerge) też wygrywają. Ten domyślny rozmiar
      // działa WYŁĄCZNIE tam, gdzie nie ma żadnej z tych rzeczy — a wtedy SVG
      // z samym viewBox rozciąga się na szerokość kontenera i rozwala layout.
      className={cn("w-4 h-4", className)}
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}
